import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type ToastTone = "success" | "error" | "info";

export type ToastRecord = {
  id: string;
  tone: ToastTone;
  message: string;
  duration: number;
};

type ToastApi = {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToastContext(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside a ToastProvider");
  return ctx;
}

function makeId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback((tone: ToastTone, message: string, duration = 4500) => {
    const id = makeId();
    const toast: ToastRecord = { id, tone, message, duration };
    setToasts((t) => [...t, toast]);
    const handle = window.setTimeout(() => removeToast(id), duration);
    timers.current.set(id, handle);
  }, [removeToast]);

  useEffect(() => {
    return () => {
      for (const h of timers.current.values()) window.clearTimeout(h);
      timers.current.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({
    success: (message: string, duration?: number) => push("success", message, duration),
    error: (message: string, duration?: number) => push("error", message, duration),
    info: (message: string, duration?: number) => push("info", message, duration),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Toast container (ARIA live region) */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed right-6 bottom-6 z-[1500] flex flex-col items-end gap-3"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className="max-w-sm w-full transform-gpu rounded-xl border px-4 py-3 shadow-md backdrop-blur-sm transition-all duration-200"
            style={{
              background: "var(--dashboard-surface-container)",
              borderColor: "var(--dashboard-outline)",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                aria-hidden
                className="mt-0.5 shrink-0 rounded-full p-1.5"
                style={{
                  border: "2px solid transparent",
                  background: "transparent",
                }}
              >
                <span
                  className="material-symbols-outlined text-base"
                  style={{
                    color:
                      toast.tone === "success"
                        ? "var(--dashboard-success)"
                        : toast.tone === "error"
                        ? "#F97373"
                        : "var(--dashboard-primary)",
                  }}
                >
                  {toast.tone === "success" ? "check_circle" : toast.tone === "error" ? "error" : "info"}
                </span>
              </div>

              <div className="min-w-0">
                <div className="text-sm font-medium" style={{ color: "var(--dashboard-on-surface)" }}>{toast.message}</div>
              </div>

              <button
                aria-label="Dismiss"
                onClick={() => removeToast(toast.id)}
                className="ml-2 -mr-1 h-8 w-8 shrink-0 rounded-full text-slate-400 transition hover:text-slate-600"
                style={{ background: "transparent" }}
              >
                <span className="material-symbols-outlined text-[1rem]">close</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
